import { useState } from 'react';
import { TruckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../../shared/ModalShell';
import { useUpdatePurchaseOrderLine } from '../../../hooks/usePurchaseOrders';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { PurchaseOrderLine } from '../../../services/purchaseOrders';
import { ModalField, MODAL_INPUT_CLASSES } from '../../shared/ModalField';
import { formatMoneyCents } from '../../../utils/money';

function todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
}

function LineSummary({ line }: { line: PurchaseOrderLine }) {
    const expected =
        line.expectedUnitCostCents === null
            ? null
            : formatMoneyCents(line.expectedUnitCostCents * line.quantity);

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex flex-col gap-1">
            <p className="text-sm font-semibold text-gray-900">{line.productName}</p>
            {line.description && <p className="text-xs text-gray-500">{line.description}</p>}
            <div className="flex items-center gap-4 mt-1 text-xs">
                <span className="text-gray-500">
                    Ordered <span className="font-semibold text-gray-800">{line.quantity}</span>
                </span>
                <span className="text-gray-500">
                    Expected{' '}
                    <span className="font-semibold text-gray-800">{expected ?? 'not costed'}</span>
                </span>
            </div>
        </div>
    );
}

interface MarkShippedModalProps {
    purchaseOrderId: string;
    line: PurchaseOrderLine;
    onClose: () => void;
}

export function MarkShippedModal({ purchaseOrderId, line, onClose }: MarkShippedModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: updateLine, isPending } = useUpdatePurchaseOrderLine();

    const [shippedQuantity, setShippedQuantity] = useState(
        String(line.shippedQuantity > 0 ? line.shippedQuantity : line.quantity)
    );
    const [shippedAt, setShippedAt] = useState(line.shippedAt?.slice(0, 10) ?? todayIsoDate());
    const [trackingNumber, setTrackingNumber] = useState(line.trackingNumber ?? '');

    const quantity = Number(shippedQuantity);
    const quantityValid = Number.isInteger(quantity) && quantity >= 0 && quantity <= line.quantity;
    // Quantity 0 un-ships the line, and the server rejects a date without a
    // quantity - so a date is only required when something actually shipped.
    const isUnshipping = quantityValid && quantity === 0;
    const canSubmit = quantityValid && (isUnshipping || shippedAt.length > 0) && !isPending;

    const handleSubmit = async () => {
        try {
            await updateLine({
                id: purchaseOrderId,
                lineId: line.id,
                body: {
                    shippedQuantity: quantity,
                    shippedAt: isUnshipping ? undefined : shippedAt,
                    // '' clears; undefined would leave the stored value alone.
                    trackingNumber: isUnshipping ? '' : trackingNumber.trim()
                }
            });
            showToast(isUnshipping ? 'Shipment cleared' : 'Shipping updated', 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to update shipping'), 'error');
        }
    };

    return (
        <ModalShell
            widthClassName="max-w-[520px]"
            panelClassName="rounded-lg p-6"
            dismissible={!isPending}
            onDismiss={onClose}
            labelledBy="po-shipped-title"
        >
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <TruckIcon className="h-4 w-4 text-gray-600" />
                    </span>
                    <h2 id="po-shipped-title" className="text-xl font-bold text-gray-900">
                        {line.shippedQuantity > 0 ? 'Update shipping' : 'Mark as shipped'}
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isPending}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            <LineSummary line={line} />

            <div className="mt-5 flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                    <ModalField
                        htmlFor="po-shipped-quantity"
                        label="Shipped quantity"
                        required
                        hintTone={quantityValid ? 'warning' : 'error'}
                        hint={
                            !quantityValid
                                ? `Enter 0–${line.quantity}.`
                                : isUnshipping
                                  ? 'Clears the shipment.'
                                  : undefined
                        }
                    >
                        <input
                            id="po-shipped-quantity"
                            type="number"
                            min={0}
                            max={line.quantity}
                            step={1}
                            value={shippedQuantity}
                            onChange={(e) => setShippedQuantity(e.target.value)}
                            className={MODAL_INPUT_CLASSES}
                        />
                    </ModalField>

                    <ModalField
                        htmlFor="po-shipped-at"
                        label="Shipped date"
                        required={!isUnshipping}
                    >
                        <input
                            id="po-shipped-at"
                            type="date"
                            value={isUnshipping ? '' : shippedAt}
                            disabled={isUnshipping}
                            onChange={(e) => setShippedAt(e.target.value)}
                            className={MODAL_INPUT_CLASSES}
                        />
                    </ModalField>
                </div>

                <ModalField htmlFor="po-tracking" label="Tracking number">
                    <input
                        id="po-tracking"
                        type="text"
                        value={isUnshipping ? '' : trackingNumber}
                        disabled={isUnshipping}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="e.g. 1Z999AA10123456784"
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>
            </div>

            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isPending}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => {
                        void handleSubmit();
                    }}
                    disabled={!canSubmit}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isPending ? 'Saving…' : isUnshipping ? 'Clear shipment' : 'Save shipping'}
                </button>
            </div>
        </ModalShell>
    );
}
